import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { http } from "../services/http";
import { tokenStore } from "../services/token";
import { authApi } from "../services/auth";
import { guestCart } from "../services/guestCart";

/* ===================== TYPES ===================== */
type ProfileResponse = {
  user: {
    userId: number;
    username: string;
    email: string;
  };
  pointValue: number;
  availablePoints: number;
  pointToMoneyRate: number;
};

export interface User {
  userId: number;
  username: string;
  email: string;
  role: string | null; // "Admin" | "Customer"...

  // ✅ thêm điểm để Header dùng
  availablePoints?: number;
  pointValue?: number;
  pointToMoneyRate?: number;
}

/* Guest cart item */
interface GuestCartItem {
  variantId: number;
  quantity: number;
}
interface GuestCart {
  items: GuestCartItem[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthed: boolean;

  setUser: (user: User | null) => void;

  // ✅ reload user (cache + profile)
  reloadUser: () => Promise<User | null>;

  // ✅ reload riêng profile/points (Header dùng)
  reloadProfile: () => Promise<User | null>;

  logout: () => void;
}

/* ===================== CONTEXT ===================== */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* ===================== STORAGE HELPERS ===================== */
const USER_KEY = "auth_user";

const saveUserCache = (u: User | null) => {
  if (!u) localStorage.removeItem(USER_KEY);
  else localStorage.setItem(USER_KEY, JSON.stringify(u));
};

const readUserCache = (): User | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
};

/* ===================== PROVIDER ===================== */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, _setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // ✅ isAuthed chỉ dựa token
  const isAuthed = !!tokenStore.getAccess();

  const setUser = (u: User | null) => {
    _setUser(u);
    saveUserCache(u);
  };

  /* =====================
     MERGE GUEST CART (SAU LOGIN)
  ===================== */
  const mergeGuestCart = async () => {
    const merged = localStorage.getItem("guest_cart_merged");
    if (merged === "1") return;

    const guest = guestCart.get() as GuestCart;
    if (!guest?.items || guest.items.length === 0) return;

    try {
      await http.post("/api/cart/merge", {
        items: guest.items.map((i: GuestCartItem) => ({
          variantId: i.variantId,
          quantity: i.quantity,
        })),
      });

      guestCart.clear();
      localStorage.setItem("guest_cart_merged", "1");
    } catch (err) {
      console.error("Merge guest cart failed", err);
    }
  };

  /* =====================
     LOAD PROFILE (POINTS)
  ===================== */
  const fetchProfile = async (): Promise<Partial<User> | null> => {
    try {
      // ✅ backend bạn có: GET /api/Users/profile (Authorize)
      const res = await http.get<ProfileResponse>("/api/Users/profile");

      const p = res.data;
      return {
        userId: p.user.userId,
        username: p.user.username,
        email: p.user.email,
        availablePoints: p.availablePoints,
        pointValue: p.pointValue,
        pointToMoneyRate: p.pointToMoneyRate,
      };
    } catch (err) {
      // không crash app vì profile fail
      console.warn("Fetch profile failed:", err);
      return null;
    }
  };

  /* =====================
     LOAD USER (CACHE + PROFILE)
  ===================== */
  const loadUser = async (): Promise<User | null> => {
    const token = tokenStore.getAccess();
    if (!token) {
      setUser(null);
      return null;
    }

    // 1) set tạm từ cache để UI không bị trống
    const cached = readUserCache();
    if (cached) setUser(cached);

    // 2) merge cart (chỉ khi có token)
    await mergeGuestCart();

    // 3) fetch profile để có điểm + đảm bảo username/email đúng
    const profileUser = await fetchProfile();
    if (profileUser) {
      const mergedUser: User = {
        ...(cached ?? (user as any) ?? {}),
        ...profileUser,
        role: (cached?.role ?? (user?.role ?? null)),
      } as User;

      setUser(mergedUser);
      return mergedUser;
    }

    // 4) nếu profile fail => trả cache (nếu có), không thì null
    if (cached) return cached;

    setUser(null);
    return null;
  };

  const reloadUser = async () => await loadUser();

  // ✅ cho Header gọi để refresh điểm sau checkout...
  const reloadProfile = async () => {
    if (!tokenStore.getAccess()) {
      setUser(null);
      return null;
    }

    const profileUser = await fetchProfile();
    if (!profileUser) return user;

    const next: User = {
      ...(user ?? (readUserCache() ?? ({} as any))),
      ...profileUser,
      role: user?.role ?? readUserCache()?.role ?? null,
    };

    setUser(next);
    return next;
  };

  /* =====================
     LOGOUT
  ===================== */
  const logout = () => {
    authApi.logout?.();
    localStorage.removeItem("guest_cart_merged");
    localStorage.removeItem(USER_KEY);

    tokenStore.clear();
    delete (http.defaults.headers.common as any).Authorization;

    setUser(null);
  };

  /* =====================
     INIT
  ===================== */
  useEffect(() => {
    authApi.initFromStorage?.();
    loadUser().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      loading,
      isAuthed: !!tokenStore.getAccess(),
      setUser,
      reloadUser,
      reloadProfile,
      logout,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ===================== HOOK ===================== */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
